import { call, put, select } from 'redux-saga/effects';

import { NIMBLY_API_NPS_TRIGGER, NIMBLY_API_NPS_QUESTIONNAIRE, NIMBLY_API_NPS_REPORT } from '../../../constants/api';
import API from '../../helpers/api';
import * as actions from './nps.action';
import { logErrorWithContext } from '../../../utils/sentry/sentrySetup';
import { RootState } from '../reducers';
import { track } from '../../../utils/analytics';

import { selectProfile } from '../../selectors/profile';
// Locale → NPS language param, following audit-lite pattern
const LOCALE_TO_NPS_LANG: Record<string, string | undefined> = {
  en: undefined,
  id: 'bahasa',
  es: 'spanish',
  pt: 'portuguese',
  ko: 'korean',
  th: 'thai',
  km: 'khmer'
};

// ─────────────────────────────────────────────────────────────────────────────
// Lite NPS sagas
// ─────────────────────────────────────────────────────────────────────────────

export function* triggerNpsSaga(action: ReturnType<typeof actions.npsTriggerAsync.request>): Generator {
  const { respondentId, organizationID, triggerSource } = action.payload;
  try {
    const state = (yield select()) as RootState;
    const profileLanguage = selectProfile(state)?.language || 'en';
    const api = (yield call(API.withToken)) as API;
    const body = {
      respondentId,
      organizationID,
      deviceDetails: {
        osType: 'Web',
        osVersion: navigator.userAgent,
        deviceId: respondentId,
        browserType: navigator.userAgent,
        ipAddress: '',
        softwareVersion: '1.0.0'
      }
    };

    const res = (yield call([api, api.postJSON], NIMBLY_API_NPS_TRIGGER, body)) as any;
    const isEligible: boolean = res?.data?.triggerNps === true;

    track('nps_triggered', { eligible: isEligible, source: triggerSource });

    yield put(actions.npsTriggerAsync.success({ isEligible }));

    if (isEligible) {
      const shouldFetchQuestionnaire = !state.npsUser.questionnaire && !state.npsUser.isQuestionnaireLoading;

      if (shouldFetchQuestionnaire) {
        yield put(actions.npsQuestionnaireAsync.request({ language: profileLanguage }));
      }
    }
  } catch (err) {
    logErrorWithContext(err instanceof Error ? err : new Error(String(err)), 'saga_nps_trigger_error', {
      saga: 'triggerNpsSaga'
    });
    yield put(actions.npsTriggerAsync.failure({ error: 'Failed to trigger NPS' }));
  }
}

export function* fetchNpsQuestionnaireSaga(
  action: ReturnType<typeof actions.npsQuestionnaireAsync.request>
): Generator {
  const { language } = action.payload;
  try {
    const state = (yield select()) as RootState;
    const lang = LOCALE_TO_NPS_LANG[language || selectProfile(state)?.language || 'en'];

    const api = (yield call(API.withToken)) as API;
    const url = lang
      ? `${NIMBLY_API_NPS_QUESTIONNAIRE}?type=nps&lang=${encodeURIComponent(lang)}`
      : `${NIMBLY_API_NPS_QUESTIONNAIRE}?type=nps`;

    const res = (yield call([api, api.getJSON], url)) as any;

    if (!res?.data) {
      throw new Error('Empty questionnaire response');
    }

    yield put(actions.npsQuestionnaireAsync.success({ questionnaire: res.data }));
  } catch (err) {
    logErrorWithContext(err instanceof Error ? err : new Error(String(err)), 'saga_nps_questionnaire_error', {
      saga: 'fetchNpsQuestionnaireSaga'
    });
    yield put(actions.npsQuestionnaireAsync.failure({ error: 'Failed to fetch NPS questionnaire' }));
  }
}

export function* submitNpsSaga(action: ReturnType<typeof actions.npsSubmitAsync.request>): Generator {
  const payload = action.payload;
  try {
    const api = (yield call(API.withToken)) as API;
    yield call([api, api.postJSON], NIMBLY_API_NPS_REPORT, payload);

    const score = payload.responseDetails.find(d => {
      const ans = d.answer[0];
      return ans !== undefined && !isNaN(Number(ans));
    });

    track('nps_submitted', {
      source: payload.gtmTracking,
      questionCount: payload.responseDetails.length,
      score: score ? Number(score.answer[0]) : null
    });

    yield put(actions.npsSubmitAsync.success());
  } catch (err) {
    logErrorWithContext(err instanceof Error ? err : new Error(String(err)), 'saga_nps_submit_error', {
      saga: 'submitNpsSaga'
    });
    yield put(actions.npsSubmitAsync.failure({ error: 'Failed to submit NPS' }));
  }
}

export function* dismissNpsSaga(action: ReturnType<typeof actions.npsDismissAsync.request>): Generator {
  const payload = action.payload;
  try {
    const api = (yield call(API.withToken)) as API;
    yield call([api, api.postJSON], NIMBLY_API_NPS_REPORT, payload);

    const state = (yield select()) as RootState;
    track('nps_dismissed', { source: state.npsUser.triggerSource || '' });

    yield put(actions.npsDismissAsync.success());
  } catch (err) {
    logErrorWithContext(err instanceof Error ? err : new Error(String(err)), 'saga_nps_dismiss_error', {
      saga: 'dismissNpsSaga'
    });
    yield put(actions.npsDismissAsync.failure({ error: 'Failed to dismiss NPS' }));
  }
}
